/**
 * `garmentDesigns.*` wire contract (Design Studio, PRD design-studio.md).
 * Positions are ADR-0029 canvas percentages (0–100, 2dp); physical
 * dimensions cross as integer millimetres; offset notes are verbatim text.
 * All procedures sit behind `entitledProcedureFor("garment.styles")`.
 * A design belongs to a Style, not an order item (ADR-0044) — it always
 * exists the instant the style does, so there is no `create` procedure here.
 */
import { z } from "zod";
import { uuid } from "../_common.js";

export const designStatus = z.enum(["draft", "approved"]);
export const designView = z.enum(["front", "back"]);
export const designElementType = z.enum([
  "logo_print",
  "embroidery_stone",
  "button",
  "pocket",
  "placket",
  "collar",
  "cuff",
  "stitch",
  "label",
  "wash_care",
  "text",
  "trim",
  "packing",
]);

const percent = z.number().min(0).max(100);
const mm = z.number().int().min(1).max(5000);

export const elementInput = z.object({
  elementType: designElementType,
  view: designView,
  x: percent,
  y: percent,
  widthMm: mm.nullable().optional(),
  heightMm: mm.nullable().optional(),
  offsetNote: z.string().trim().max(160).nullable().optional(),
  note: z.string().trim().max(500).nullable().optional(),
  artworkObjectKey: z.string().trim().max(1024).nullable().optional(),
  colourway: z.string().trim().max(40).nullable().optional(),
});

const element = z.object({
  id: uuid,
  position: z.number().int().nonnegative(),
  elementType: designElementType,
  view: designView,
  x: z.number(),
  y: z.number(),
  widthMm: z.number().int().nullable(),
  heightMm: z.number().int().nullable(),
  offsetNote: z.string().nullable(),
  note: z.string().nullable(),
  artworkObjectKey: z.string().nullable(),
  artworkUrl: z.string().nullable(),
  colourway: z.string().nullable(),
});

const revisionSummary = z.object({
  id: uuid,
  version: z.number().int().positive(),
  note: z.string().nullable(),
  createdAt: z.string().datetime(),
  elementCount: z.number().int().nonnegative(),
  isApproved: z.boolean(),
});

/** A design belongs to ONE Style (ADR-0044). */
export const getForStyleInput = z.object({
  styleId: uuid,
  version: z.number().int().positive().optional(),
});

export const designDetail = z.object({
  id: uuid,
  styleId: uuid,
  styleRef: z.string(),
  styleStatus: z.string(),
  buyerId: uuid,
  buyerName: z.string(),
  garmentType: z.string(),
  name: z.string(),
  status: designStatus,
  archived: z.boolean(),
  approvedRevisionId: uuid.nullable(),
  revisions: z.array(revisionSummary),
  currentVersion: z.number().int().positive(),
  currentRevisionId: uuid,
  elements: z.array(element),
  createdAt: z.string().datetime(),
});
export const getForStyleOutput = designDetail;

export const saveRevisionInput = z.object({
  designId: uuid,
  note: z.string().trim().max(200).nullable().optional(),
  elements: z.array(elementInput).max(120),
});
export const saveRevisionOutput = z.object({
  designId: uuid,
  revisionId: uuid,
  version: z.number().int().positive(),
});

export const approveInput = z.object({
  designId: uuid,
  revisionId: uuid,
  note: z.string().trim().max(200).nullable().optional(),
});
export const approveOutput = z.object({
  designId: uuid,
  approvedRevisionId: uuid,
  status: designStatus,
});

export const generatePackInput = z.object({
  designId: uuid,
  version: z.number().int().positive().optional(),
});
export const generatePackOutput = z.object({
  packId: uuid,
  designId: uuid,
  revisionId: uuid,
  version: z.number().int().positive(),
  creditsDebited: z.number().int().positive(),
  balanceAfter: z.number().int(),
});

export const proposeInput = z.object({
  designId: uuid,
  prompt: z.string().trim().min(3).max(600),
  view: designView,
});
export const proposedElement = elementInput.extend({
  rationale: z.string().nullable(),
});
export const proposeOutput = z.object({
  proposals: z.array(proposedElement),
});

export const artworkContentType = z.enum(["image/jpeg", "image/png", "image/webp"]);

export const requestArtworkUploadInput = z.object({
  designId: uuid,
  contentType: artworkContentType,
  size: z.number().int().positive().max(20 * 1024 * 1024),
});
export const requestArtworkUploadOutput = z.object({
  uploadUrl: z.string(),
  objectKey: z.string(),
});

export const confirmArtworkUploadInput = z.object({
  objectKey: z.string().trim().min(1).max(1024),
});
export const confirmArtworkUploadOutput = z.object({
  objectKey: z.string(),
  artworkUrl: z.string().nullable(),
});
